import { FeedFilterBy } from '@warebee/shared/dataset-queries';
import { ActivityFeedEventDataset } from '@warebee/shared/import-converter';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { JsonObject } from 'type-fest';
import { containsFilter } from '../common/filterHelper';
import { AsyncLoadStatus, DatasetDataViewProps } from '../common/types';
import { Button } from '../components/actions/Button';
import DatasetTable, { ColumnConfig } from '../components/DatasetTable';
import { Spacer } from '../components/layout/Spacer';
import useLoadGenericDatasetData from './hooks/useLoadGenericDatasetData';
import {
  columnDisplayModeState,
  datasetObjectData,
  datasetObjectDataLoadStatus,
  datasetObjectDataState,
  datasetObjectDocument,
} from './store/datasetObject.state';

const DatasetDataTable: React.FC<
  DatasetDataViewProps<ActivityFeedEventDataset>
> = props => {
  const { t } = useTranslation('dataset');
  const dataset = useRecoilValue(datasetObjectDocument);
  const data = useRecoilValue(datasetObjectData);
  const status = useRecoilValue(datasetObjectDataLoadStatus);
  const [dataState, setDataState] = useRecoilState(datasetObjectDataState);
  const [columnDisplayMode, setColumnDisplayMode] = useRecoilState(
    columnDisplayModeState,
  );
  const [datasetDataLoad, datasetDataReset, exportCSV] =
    useLoadGenericDatasetData();

  const columnsConfig: ColumnConfig<JsonObject>[] = _.map(
    data.columns,

    column => ({
      // hasSort: true,
      field: _.camelCase(column.name),
      title:
        columnDisplayMode === 'formatted'
          ? _.startCase(_.replace(column.name, '_', ' '))
          : column.name,
      hasFilter: column.type === 'varchar',
      // Store raw name for field summary matching
      rawName: column.name,
    }),
  ) as any;

  const datasetId = dataset.id;
  useEffect(() => {
    callDataLoad(dataState.searchValues);
  }, [props.id, dataState.searchValues]);

  useEffect(() => {
    return () => {
      datasetDataReset();
    };
  }, []);

  function callDataLoad(
    searchValues: Record<string, string>,
    page = { isAppend: false, skip: 0 },
  ) {
    if (_.isNil(props.id)) return;

    const filterBy: FeedFilterBy = _.reduce(
      searchValues,
      (acc, value, key) => ({ ...acc, [key]: containsFilter(value) }),
      {},
    );

    datasetDataLoad({
      datasetId,
      isAppend: page.isAppend,
      page: {
        skip: page.skip,
      },
      filterBy,
    });
  }

  function onLoadNext() {
    callDataLoad(dataState.searchValues, {
      isAppend: true,
      skip: itemsCount,
    });
  }

  async function startExportCSV() {
    return await exportCSV({
      datasetId,
      columnsConfig,
    });
  }

  const isLoading = status === AsyncLoadStatus.Loading;
  const itemsCount = data?.content?.length || 0;
  const totalCount = data?.totalCount ?? 0;

  // if (props.customRenders) {
  //   _.forEach(props.customRenders, (renderer, key) => {
  //     const cfg = _.find(columnsConfig, c => c.field === key);
  //     if (!_.isNil(cfg)) {
  //       cfg.render = renderer;
  //     }
  //   });
  // }

  return (
    <>
      <DatasetTable
        id={props.id}
        data={data?.content}
        columnsConfig={columnsConfig}
        keyFields={['eventId']}
        hasCounter
        hideScreenTitle={props.hideScreenTitle}
        subtitle={t`Datasets`}
        title={props.title}
        isSticky
        onLoadNext={onLoadNext}
        onSearch={v => {
          // const transformedValues = _.mapKeys(v, (value, key) => {
          //   const column = _.find(columnsConfig, c => c.field === key);
          //   return column?.rawName || key;
          // });
          const transformedValues = _.mapKeys(v, (value, key) =>
            _.camelCase(key),
          );

          setDataState({ ...dataState, searchValues: transformedValues });
        }}
        totalCount={totalCount}
        searchValues={dataState.searchValues}
        isLoading={isLoading}
        onStartExportClick={startExportCSV}
        // fieldsSummary={dataset?.summary?.fields}
        fieldsSummary={dataset?.summary?.fields?.map(field => ({
          ...field,
          name:
            _.find(columnsConfig, c => c.rawName === field.name)?.field ||
            field.name,
        }))}
        actionSection={
          <>
            <Button
              className={classNames(
                'hover:text-menu-active',
                'bg-menu/50 hover:bg-menu',
                'text-xxs',
                'flex items-center',
                'rounded-full',
                'px-2 py-1',
                'focus:outline-none focus:ring-0',
              )}
              buttonSize="xs"
              hasIconAfter
              label={columnDisplayMode === 'formatted' ? t`Raw` : t`Formatted`}
              onPress={() =>
                setColumnDisplayMode(mode =>
                  mode === 'formatted' ? 'raw' : 'formatted',
                )
              }
            />
            <Spacer flexspace />
          </>
        }
      />
    </>
  );
};

export default DatasetDataTable;
